import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { IScreen } from '@prizm-ui/components';
import { CommonModule, NgTemplateOutlet } from '@angular/common';
import { TuiButton, TuiHint, TuiIcon } from '@taiga-ui/core';
import { TuiHintDirection } from '@taiga-ui/core/directives/hint';
import { MenuStateService } from './service/menu-state.service';
import { SmaPageMenuStore } from './store/nav-menu-store';
import { IClsMenuItem } from './store/nav-menu-schema';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';
import build from '../../../build-version.json';

const VERSION_HINT_APPEARANCE = 'dark';
const VERSION_HINT_DIRECTION: TuiHintDirection = 'right';
const VERSION_HINT_TITLE = 'УЖУ';

export interface INavigation extends IScreen {
  isExpanded?: boolean | null;
  children?: INavigation[];
  route?: string;
  code?: string;
  id?: string;
  accessedRoles?: string[]; // если не задан, то доступен для всех
  icon?: string;
}

@Component({
  selector: 'uzu-nav-menu',
  standalone: true,
  templateUrl: './uzu-nav-menu.component.html',
  styleUrl: './uzu-nav-menu.component.less',
  imports: [RouterModule, NgTemplateOutlet, CommonModule, TuiIcon, TuiButton, TuiHint],
})
export class UzuNavMenuComponent implements OnInit {
  private readonly _router = inject(Router);
  private readonly _store = inject(SmaPageMenuStore);
  private readonly _state = inject(MenuStateService);
  private readonly _dr = inject(DestroyRef);

  protected username = signal('');

  protected selectedSection = 'none';

  public navigationHierarchy: INavigation[] = [];

  public isMenuCollapsed = false;

  protected readonly versionHintAppearance = VERSION_HINT_APPEARANCE;
  protected readonly versionHintDirection = VERSION_HINT_DIRECTION;

  public ngOnInit(): void {
    this._store.fetchMenu();
    this._store.mainMenu$.pipe(takeUntilDestroyed(this._dr)).subscribe((menu) => {
      this.buildMenu(menu);
    });

    this._state.isOpen$.pipe(filter((isOpen) => !isOpen)).subscribe(() => {
      this.setExpandedFalse(this.navigationHierarchy);
    });
  }

  protected handleNavigation(item: INavigation): void {
    if (!item.route) {
      return;
    }

    if (this.isExternalLink(item.route)) {
      window.open(item.route, '_blank');
    } else {
      this._router.navigate([item.route]);
    }
  }

  protected isExternalLink(route: string | undefined): boolean {
    if (!route) {
      return false;
    }
    return /^(https?:\/\/|www\.)/i.test(route);
  }

  protected toggleItem(item: INavigation): void {
    item.isExpanded = !item.isExpanded;
    this.hideOthers(item);
    if (!item.isExpanded && item.children) {
      this.setExpandedFalse(item.children);
    }
  }

  public addMenuScrollbar(id: string): void {
    setTimeout(() => {
      const collapsedMenu = document.querySelector<HTMLElement>(`#${id}`);
      const sideMenu = collapsedMenu?.querySelector<HTMLElement>('#side-menu__subitems');
      const sideMenuLevelTwo = sideMenu?.querySelector<HTMLElement>('#side-menu__subitems');

      const subitem = sideMenuLevelTwo
        ? sideMenuLevelTwo.querySelectorAll('.side-menu__subitem')
        : sideMenu?.querySelectorAll('.side-menu__subitem');
      if (subitem) {
        // eslint-disable-next-line unicorn/prefer-at
        const lastSubitem = subitem[subitem.length - 1];
        const rect = lastSubitem?.getBoundingClientRect();

        if (sideMenu && window.innerHeight - rect.bottom < 0) {
          // 40 тк высота плашки меню
          sideMenu.style.height = `${
            subitem.length * 40 + (window.innerHeight - rect.bottom - 1)
          }px`;
          sideMenu.style.overflowY = 'scroll';
          sideMenu.style.overflowX = 'hidden';
        }
        if (window.innerHeight - rect.bottom < 0 && sideMenuLevelTwo) {
          // 40 тк высота плашки меню
          sideMenuLevelTwo.style.height = `${
            subitem.length * 40 + (window.innerHeight - rect.bottom - 1)
          }px`;
          sideMenuLevelTwo.style.overflowY = 'scroll';
          sideMenuLevelTwo.style.overflowX = 'hidden';
        }
      }
    }, 0);
  }

  protected setExpandedFalse(items: INavigation[]): void {
    for (const item of items) {
      if (item.isExpanded !== null) {
        item.isExpanded = false;
      }

      if (item.children && item.children.length > 0) {
        this.setExpandedFalse(item.children);
      }
    }
  }

  protected hideOthers(itemToLookTo: INavigation): void {
    const path = this.findPath(itemToLookTo);
    this.hideSubLevels(this.navigationHierarchy, path);
  }

  protected hideSubLevels(levels: INavigation[], path: INavigation[]): void {
    for (const level of levels) {
      if (!path.includes(level)) {
        level.isExpanded = false;
      }
      if (level.children) {
        this.hideSubLevels(level.children, path);
      }
    }
  }

  protected findPath(itemToLookTo: INavigation): INavigation[] {
    const path: INavigation[] = [];
    this.findItem(this.navigationHierarchy, itemToLookTo, path);
    return path;
  }

  protected findItem(
    level: INavigation[],
    itemToLookTo: INavigation,
    path: INavigation[]
  ): boolean {
    for (const item of level) {
      if (
        item === itemToLookTo ||
        (item.children && this.findItem(item.children, itemToLookTo, path))
      ) {
        path.push(item);
        return true;
      }
    }
    return false;
  }

  protected get version(): string {
    return build.buildVersion;
  }

  protected get versionHint(): string {
    return `${VERSION_HINT_TITLE}: ${this.version}`;
  }

  private buildMenu(menu: IClsMenuItem[]): void {
    const sortedMenuItems = [...menu].sort((a, b) => (a.order_num ?? 0) - (b.order_num ?? 0));

    const menuHash: Record<string, INavigation> = {};
    for (const item of sortedMenuItems) {
      menuHash[item.id] = {
        title: item.title ?? '',
        route: item.route ?? '',
        id: item.id,
        children: [],
        code: item.code,
        icon: item.icon,
        isExpanded: false,
        accessedRoles: undefined,
      };
    }

    const rootMenuItems: INavigation[] = [];
    for (const item of sortedMenuItems) {
      if (item.id_parent && menuHash[item.id_parent]) {
        menuHash[item.id_parent].children?.push(menuHash[item.id]);
      } else {
        rootMenuItems.push(menuHash[item.id]);
      }
    }

    this.navigationHierarchy = rootMenuItems;
  }

  public toggleMenu(): void {
    this.isMenuCollapsed = !this.isMenuCollapsed;
    if (this.isMenuCollapsed) {
      this.setExpandedFalse(this.navigationHierarchy);
    }
  }
}
